import {
  FIELD_MIN_HEIGHT,
  FIELD_MIN_WIDTH,
  FORM_FIELD,
  type TextBoxInputType,
} from '@/constants/constants';
import { IFileInfo } from '@/interfaces/interfaces';
import { IPlaceholderField } from '@/interfaces/placeholder-field';
import { getFieldFontSize } from '@/utils/field-font-size';

/**
 * Standard field types that support "Select all of this type" bulk editing.
 * Signature, board and prefilled fields are deliberately left out for now.
 */
export const BULK_EDITABLE_ITEM_IDS: readonly string[] = [
  FORM_FIELD.TEXT_BOX,
  FORM_FIELD.TEXT_AREA,
  FORM_FIELD.CHECKBOX,
  FORM_FIELD.DATE_PICKER,
  FORM_FIELD.SIGN_DATE,
  FORM_FIELD.DROPDOWN,
];

const FIELD_TYPE_LABELS: Record<string, { one: string; many: string }> = {
  [FORM_FIELD.TEXT_BOX]: { one: 'Text box', many: 'Text boxes' },
  [FORM_FIELD.TEXT_AREA]: { one: 'Text area', many: 'Text areas' },
  [FORM_FIELD.CHECKBOX]: { one: 'Checkbox', many: 'Checkboxes' },
  [FORM_FIELD.DATE_PICKER]: { one: 'Date field', many: 'Date fields' },
  [FORM_FIELD.SIGN_DATE]: { one: 'Sign date field', many: 'Sign date fields' },
  [FORM_FIELD.DROPDOWN]: { one: 'Dropdown', many: 'Dropdowns' },
};

/** Checkboxes are small squares, so they keep a smaller floor than text fields. */
const CHECKBOX_MIN_SIZE = 8;

/** Which bulk edit controls apply to a field type. */
export interface BulkEditCapabilities {
  fontSize: boolean;
  required: boolean;
  inputType: boolean;
  assignTo: boolean;
}

export const getBulkEditCapabilities = (
  itemId: string,
): BulkEditCapabilities => ({
  fontSize: itemId !== FORM_FIELD.CHECKBOX,
  required: [
    FORM_FIELD.TEXT_BOX,
    FORM_FIELD.CHECKBOX,
    FORM_FIELD.DATE_PICKER,
    FORM_FIELD.DROPDOWN,
  ].includes(itemId),
  inputType: itemId === FORM_FIELD.TEXT_BOX,
  // Text areas are filled in by the sender, so they have no signer.
  assignTo: itemId !== FORM_FIELD.TEXT_AREA,
});

export const isBulkEditableField = (
  field: Pick<IPlaceholderField, 'itemId'> | null | undefined,
): boolean => !!field && BULK_EDITABLE_ITEM_IDS.includes(field.itemId);

export const getFieldTypeLabel = (itemId: string, count: number): string => {
  const labels = FIELD_TYPE_LABELS[itemId] ?? { one: 'Field', many: 'Fields' };
  return count === 1 ? labels.one : labels.many;
};

export interface SameTypeFields {
  /** Count across every file in the template. */
  templateCount: number;
  /** Ids on the anchor field's page, in the active file. */
  pageIds: string[];
  /** Ids on every page of the active file. */
  documentIds: string[];
}

/**
 * Fields sharing the anchor field's type. Only fields of the active file can be
 * selected, because fields in other files are not visible while editing.
 */
export const getSameTypeFields = (
  files: IFileInfo[],
  activeFileId: string,
  anchor: IPlaceholderField,
): SameTypeFields => {
  const isSameType = (field: IPlaceholderField) =>
    field.itemId === anchor.itemId;

  const templateCount = files.reduce(
    (count, file) => count + file.fields.filter(isSameType).length,
    0,
  );
  const activeFields =
    files.find((file) => file._id === activeFileId)?.fields ?? [];
  const documentFields = activeFields.filter(isSameType);

  return {
    templateCount,
    pageIds: documentFields
      .filter(
        (field) => field.formField.pageNumber === anchor.formField.pageNumber,
      )
      .map((field) => field.id),
    documentIds: documentFields.map((field) => field.id),
  };
};

export const MIXED = Symbol('mixed');
export type Mixed = typeof MIXED;

/** The value every field shares, or MIXED when they differ. */
export const getSharedValue = <T>(
  fields: IPlaceholderField[],
  getValue: (field: IPlaceholderField) => T,
): T | Mixed | undefined => {
  if (!fields.length) return undefined;
  const first = getValue(fields[0]);
  return fields.every((field) => getValue(field) === first) ? first : MIXED;
};

export const getSharedFontSize = (fields: IPlaceholderField[]) =>
  getSharedValue(fields, (field) => getFieldFontSize(field));

export interface BulkFieldPatch {
  width?: number;
  height?: number;
  fontSize?: number;
  required?: boolean;
  inputType?: TextBoxInputType;
}

/**
 * Applies a bulk patch to one field. Sizes keep the field's top-left corner,
 * never drop below the resize floor, and never run past the page edge.
 */
export const applyBulkPatch = (
  field: IPlaceholderField,
  patch: BulkFieldPatch,
  page: { width: number; height: number } | undefined,
): IPlaceholderField => {
  const minWidth =
    field.itemId === FORM_FIELD.CHECKBOX ? CHECKBOX_MIN_SIZE : FIELD_MIN_WIDTH;
  const minHeight =
    field.itemId === FORM_FIELD.CHECKBOX ? CHECKBOX_MIN_SIZE : FIELD_MIN_HEIGHT;

  const fitSize = (value: number, min: number, available?: number) => {
    const capped =
      available !== undefined && available > 0
        ? Math.min(value, available)
        : value;
    return Math.max(capped, min);
  };

  const updated: IPlaceholderField = { ...field };
  if (patch.width !== undefined) {
    updated.width = fitSize(
      patch.width,
      minWidth,
      page ? page.width - field.placeholder.x : undefined,
    );
  }
  if (patch.height !== undefined) {
    updated.height = fitSize(
      patch.height,
      minHeight,
      page ? page.height - field.placeholder.y : undefined,
    );
  }
  if (patch.fontSize !== undefined) {
    updated.fontStyle = { ...field.fontStyle, fontSize: patch.fontSize };
  }
  if (patch.required !== undefined) {
    updated.required = patch.required;
  }
  if (patch.inputType !== undefined) {
    updated.inputType = patch.inputType;
  }
  return updated;
};
